import React, { FunctionComponent, useEffect, useState } from "react";
import { Box, Card, DialogContent, Skeleton, Toolbar } from "@mui/material";
import { strings } from "../../../localization/LocalizedStrings";
import styles from "./styles.module.scss";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../hooks/isMobile";
import { PasskeyItem } from "../PasskeyItem";
import { AlertDialog } from "../../AlertDialog";
import { ContentUnavailableNotice } from "../../attributes/ContentUnavailableNotice";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons";
import { IPasskey, useGetPasskeys } from "../../../hooks/passkeys/useGetPasskeys";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { useDeletePasskey } from "../../../hooks/passkeys/useDeletePasskey";
import { usePasskeys } from "../../../hooks/passkeys";
import { Environment } from "../../../config";

export const PasskeysDialog: FunctionComponent<IPasskeysDialogProps> = ({ open, setOpen }) => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [passkeys, setPasskeys] = useState<IPasskey[]>();
  const { registerPasskey } = usePasskeys();
  const { getPasskeys } = useGetPasskeys();
  const { deletePasskey } = useDeletePasskey();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [idToDelete, setIdToDelete] = useState<string>();
  const [isDeleteConfirmationAlertOpen, setIsDeleteConfirmationAlertOpen] = useState(false);
  useEffect(() => {
    fetchPasskeys().then();
  }, [open]);
  const fetchPasskeys = async () => {
    setLoading(true);
    const { status, body } = await getPasskeys();
    if (StatusCodesHelper.isSuccessful(status)) {
      setPasskeys(body);
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setLoading(false);
  };
  const removePasskey = async () => {
    setLoading(true);
    const { status, body } = await deletePasskey({ id: idToDelete });
    if (StatusCodesHelper.isSuccessful(status)) {
      setPasskeys(undefined);
      if (typeof PublicKeyCredential.getClientCapabilities === "function") {
        const capabilities = await PublicKeyCredential.getClientCapabilities();
        if (capabilities.signalUnknownCredential) {
          await (PublicKeyCredential as any).signalUnknownCredential({
            rpId: Environment.relyingParty,
            credentialId: idToDelete
          });
        }
      }
      await fetchPasskeys();
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setLoading(false);
  };
  const registerNewPasskey = async () => {
    await registerPasskey(
      () => {
        fetchPasskeys().then();
      },
      message => {
        setAlertMessage(message);
        setIsAlertOpen(true);
      }
    );
  };
  return (
    <PiperDialog fullWidth maxWidth={"sm"} open={open} onClose={() => setOpen(false)}>
      <Toolbar>
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <h3 style={{ marginRight: 8 }}>{strings.passkeys_title}</h3>
            <ToolbarButton
              onClick={registerNewPasskey}
              tooltip={strings.add_passkey}
              iconType={IconType.AddCircle}
            />
            <Box flexGrow={1} />
            <ToolbarButton
              loading={loading}
              onClick={() => setOpen(false)}
              tooltip={strings.close}
              iconType={IconType.Close}
            />
          </div>
        </div>
      </Toolbar>
      <p className={styles.description}>{strings.passkeys_description}</p>
      <Card className={styles.contentCard}>
        <DialogContent sx={{ padding: isMobile ? 0 : 1 }}>
          {passkeys &&
            passkeys.map(passkey => (
              <PasskeyItem
                id={passkey.id}
                key={passkey.id}
                createdUnixTimestamp={passkey.createdUnixTimestamp}
                onDelete={id => {
                  setIdToDelete(id);
                  setIsDeleteConfirmationAlertOpen(true);
                }}
              />
            ))}
          {loading &&
            Array.from({ length: 3 }, (value, index) => (
              <div key={index} className={styles.cardContainer}>
                <Skeleton height={60} variant={"rectangular"} className={styles.passkeyCard} />
              </div>
            ))}
        </DialogContent>
        <ContentUnavailableNotice
          isLoading={loading}
          items={passkeys}
          emptyIconType={IconType.Passkey}
          emptyTitle={strings.empty_passkeys}
          emptyDescription={strings.empty_attribute_description}
          searchText={undefined}
          canAdd={true}
          addButtonTitle={strings.add_passkey}
          onAddItem={registerNewPasskey}
        />
      </Card>
      <AlertDialog
        title={strings.passkeys_title}
        message={alertMessage}
        open={isAlertOpen}
        setOpen={setIsAlertOpen}
        maxWidth={"xs"}
      />
      <AlertDialog
        message={strings.passkeys_delete_confirmation_message}
        open={isDeleteConfirmationAlertOpen}
        setOpen={setIsDeleteConfirmationAlertOpen}
        isConfirm
        onConfirm={removePasskey}
      />
    </PiperDialog>
  );
};

export interface IPasskeysDialogProps {
  open: boolean;
  setOpen: (state: boolean) => void;
}
